import type { PlanFrequency, PlanItem, PlanTimeBlock } from '@/api/types';

const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};
const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/** "Mon 09:00–13:00 · Wed 14:00–16:00" */
export function describeBlocks(blocks: PlanTimeBlock[]): string {
  if (!blocks || blocks.length === 0) return '';
  const byDay = new Map<string, string[]>();
  for (const block of blocks) {
    const list = byDay.get(block.day) ?? [];
    list.push(`${block.start}–${block.end}`);
    byDay.set(block.day, list);
  }
  return DAY_ORDER.filter((day) => byDay.has(day))
    .map((day) => `${DAY_LABELS[day]} ${byDay.get(day)!.join(', ')}`)
    .join(' · ');
}

/** How often a task happens, in words, from either the structured or the old shape. */
export function describeFrequency(frequency: PlanFrequency | string | undefined): string {
  if (!frequency) return '';
  if (typeof frequency === 'string') return frequency;
  switch (frequency.kind) {
    case 'every_visit':
      return 'Every visit';
    case 'as_needed':
      return 'As needed';
    case 'once':
      return 'Once';
    case 'days': {
      if (frequency.text) return frequency.text;
      if (frequency.blocks?.length) return describeBlocks(frequency.blocks);
      if (frequency.days?.length) {
        return DAY_ORDER.filter((d) => frequency.days!.includes(d))
          .map((d) => DAY_LABELS[d])
          .join(', ');
      }
      return 'Follows the plan schedule';
    }
    default:
      return '';
  }
}

/**
 * One line describing a plan item, for the places that list a plan rather than
 * edit it.
 *
 * Every section used to render `item.name`, which is empty for a schedule entry
 * — it holds blocks, not a name — so a saved schedule displayed as a dash and
 * read as nothing recorded. Tasks fared little better: a name alone says
 * nothing about how often or under which service.
 *
 * A schedule is shown as text rather than a grid on purpose. The calendar is
 * for drawing a week; reading one back is faster in a sentence.
 */
export function describePlanItem(section: string, item: PlanItem): { title: string; note?: string } {
  if (section === 'schedule') {
    const blocks = (item.blocks as PlanTimeBlock[] | undefined) ?? [];
    if (blocks.length > 0) return { title: describeBlocks(blocks) };
    // Plans written before blocks existed.
    const days = (item.days ?? []) as string[];
    const window = typeof item.window === 'string' ? item.window : '';
    if (days.length > 0) {
      const named = DAY_ORDER.filter((d) => days.includes(d)).map((d) => DAY_LABELS[d]).join(', ');
      return { title: window ? `${named} ${window}` : named };
    }
    return { title: 'No times set' };
  }

  if (section === 'tasks') {
    const frequency = describeFrequency(item.frequency);
    const parts = [frequency, item.service_name as string | undefined].filter(Boolean);
    return { title: item.name ?? 'Unnamed task', note: parts.join(' · ') || undefined };
  }

  const detail = (item.detail ?? item.instructions) as string | undefined;
  return { title: item.name ?? '—', note: detail };
}
